import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Quill from 'quill';
import { EditorCont } from './Styles';
import logger from 'utils/logger';

export const ANCHOR_TAG = 'A';

const propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

const log = logger.createLogger('TextEditor');

const TextEditor = ({
  className,
  placeholder,
  defaultValue,
  // we're not really feeding new value to quill instance on each render because it's too
  // expensive, but we're still accepting 'value' prop as alias for defaultValue because
  // other components like <Form.Field> feed their children with data via the 'value' prop
  value: alsoDefaultValue,
  onChange,
}) => {
  const $editorContRef = useRef();
  const $editorRef = useRef();
  const quillRef = useRef(null); // Add this to track Quill instance

  const initialValueRef = useRef(defaultValue || alsoDefaultValue || '');

  useEffect(() => {
    const handleContentsChange = () => {
      onChange(getHTMLValue());
    };

    const getHTMLValue = () =>
      $editorContRef.current?.querySelector('.ql-editor')?.innerHTML;

    // Function to convert URLs to links
    const convertUrlsToLinks = () => {
      const text = quillRef.current.getText();
      const urlMatcher = /(https?:\/\/[^\s]+)/g;
      const matches = text.match(urlMatcher);

      if (matches) {
        const selection = quillRef.current.getSelection();

        matches.forEach((url) => {
          // Find the URL in the text and format it as a link
          const text = quillRef.current.getText();
          const index = text.indexOf(url);
          if (index !== -1) {
            quillRef.current.formatText(index, url.length, 'link', url);
          }
        });

        // Restore selection
        if (selection) {
          quillRef.current.setSelection(selection);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (!$editorRef.current || quillRef.current) {
        return;
      }

      const quillConfig = {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote'], // 'code-block' is not needed
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            ['link'],
            ['clean'],
          ],
        },
      };

      quillRef.current = new Quill($editorRef.current, {
        placeholder,
        ...quillConfig,
      });

      // Adds filter to add empty when the clipboard contains an image or a picture
      quillRef.current.clipboard.addMatcher('IMG', () => {
        const Delta = Quill.import('delta');
        return new Delta().insert('');
      });
      quillRef.current.clipboard.addMatcher('PICTURE', () => {
        const Delta = Quill.import('delta');
        return new Delta().insert('');
      });

      const insertInitialValue = () => {
        quillRef.current.clipboard.dangerouslyPasteHTML(
          0,
          initialValueRef.current
        );
        quillRef.current.blur();
      };

      insertInitialValue();

      quillRef.current.on('text-change', (delta, oldDelta, source) => {
        if (source === 'user') {
          clearTimeout(quillRef.current.urlConversionTimer);
          quillRef.current.urlConversionTimer = setTimeout(() => {
            convertUrlsToLinks();
          }, 500);
        }

        handleContentsChange();
      });
    }, 0);

    return () => {
      clearTimeout(timer);
      if (quillRef.current) {
        quillRef.current.off('text-change', handleContentsChange);
        if (quillRef.current.root) {
          quillRef.current.root.removeEventListener('click', (e) => {
            if (e.target.tagName === ANCHOR_TAG) {
              e.preventDefault();
              window.open(e.target.href, '_blank', 'noopener,noreferrer');
            }
          });
        }
        quillRef.current = null;
      }
    };
  }, []);

  return (
    <EditorCont className={className} ref={$editorContRef}>
      <div ref={$editorRef} />
    </EditorCont>
  );
};

TextEditor.propTypes = propTypes;

export default TextEditor;
